import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, ShieldCheck, ArrowLeft, CheckCircle2, Lock, BadgeCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

// Critères par rôle (spec audit §6.2 / §6.3)
const CRITERIA_STAND_RATES_FESTIVAL = [
  { id: 'organisation', label: 'Organisation générale' },
  { id: 'communication', label: 'Communication de l\'orga' },
  { id: 'affluence', label: 'Affluence conforme à l\'annonce' },
  { id: 'logistique', label: 'Logistique (accès, électricité, eau)' },
];
const CRITERIA_FESTIVAL_RATES_STAND = [
  { id: 'qualite', label: 'Qualité du stand / des produits' },
  { id: 'ponctualite', label: 'Ponctualité (installation, départ)' },
  { id: 'consignes', label: 'Respect des consignes' },
  { id: 'relationnel', label: 'Relationnel avec l\'équipe' },
];

function StarRating({ value, onChange, size = 'md' }: { value: number; onChange: (v: number) => void; size?: 'md' | 'lg' }) {
  const [hover, setHover] = useState(0);
  const px = size === 'lg' ? 'w-9 h-9' : 'w-6 h-6';
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110"
        >
          <Star className={`${px} transition-colors ${
            n <= (hover || value) ? 'fill-[#FF5C00] text-[#FF5C00]' : 'text-gray-700'
          }`} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [appData, setAppData] = useState<any>(null);
  const [festData, setFestData] = useState<any>(null);
  const [isOrga, setIsOrga] = useState(false);

  const [rating, setRating] = useState(0);
  const [criteria, setCriteria] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');

  useEffect(() => {
    async function load() {
      if (!user || !applicationId) return;
      try {
        const appSnap = await getDoc(doc(db, 'applications', applicationId));
        if (!appSnap.exists()) { toast.error('Candidature introuvable'); navigate(-1); return; }
        const a = appSnap.data();
        setAppData(a);

        const festSnap = await getDoc(doc(db, 'festivals', a.festivalId));
        if (festSnap.exists()) {
          const f = festSnap.data();
          setFestData(f);
          setIsOrga(user.uid === f.ownerId);
        }

        // Déjà noté ?
        const token = await user.getIdToken();
        const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/reviews/${applicationId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const j = await resp.json();
          setAlreadyReviewed(j.alreadyReviewed);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user, applicationId, navigate]);

  const criteriaList = isOrga ? CRITERIA_FESTIVAL_RATES_STAND : CRITERIA_STAND_RATES_FESTIVAL;
  const targetName = isOrga ? (appData?.standName || 'ce stand') : (festData?.name || 'ce festival');

  const handleSubmit = async () => {
    if (!user || !applicationId || rating === 0) {
      toast.error('Choisissez une note globale');
      return;
    }
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ applicationId, rating, criteria, comment }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      setDone(true);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#0D0D0F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
    </div>;
  }

  if (done || alreadyReviewed) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-black mb-3">
            {done ? 'Merci pour votre avis !' : 'Vous avez déjà noté cette participation'}
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            Votre avis restera confidentiel jusqu'à ce que l'autre partie ait aussi noté
            (ou au bout de 14 jours). C'est le principe de la notation double aveugle :
            personne ne peut ajuster sa note en fonction de l'autre.
          </p>
          <button
            onClick={() => navigate(isOrga ? '/my-festivals' : '/stand/festivals')}
            className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
          >
            Retour à mes {isOrga ? 'festivals' : 'candidatures'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      <div className="max-w-xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-white text-sm font-medium mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="mb-8">
          <h1 className="text-2xl font-black tracking-tight mb-2">
            Notez {isOrga ? 'le stand' : 'votre expérience à'} <span className="text-[#FF5C00]">{targetName}</span>
          </h1>
          <div className="flex items-start gap-2.5 bg-[#161618] border border-white/8 rounded-xl px-4 py-3 mt-4">
            <Lock className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
            <p className="text-xs text-gray-400 leading-relaxed">
              <strong className="text-blue-300">Notation double aveugle</strong> — votre avis reste
              confidentiel jusqu'à ce que l'autre partie ait aussi noté. Impossible d'ajuster sa note
              en fonction de l'autre.
            </p>
          </div>
        </div>

        {/* Note globale */}
        <div className="bg-[#161618] border border-white/8 rounded-2xl p-6 mb-5">
          <p className="text-sm font-black mb-4">Note globale *</p>
          <StarRating value={rating} onChange={setRating} size="lg" />
        </div>

        {/* Critères détaillés */}
        <div className="bg-[#161618] border border-white/8 rounded-2xl p-6 mb-5">
          <p className="text-sm font-black mb-1">Critères détaillés</p>
          <p className="text-xs text-gray-500 mb-5">Optionnel mais recommandé</p>
          <div className="space-y-4">
            {criteriaList.map(c => (
              <div key={c.id} className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-300">{c.label}</span>
                <StarRating value={criteria[c.id] || 0} onChange={v => setCriteria(prev => ({ ...prev, [c.id]: v }))} />
              </div>
            ))}
          </div>
        </div>

        {/* Commentaire */}
        <div className="bg-[#161618] border border-white/8 rounded-2xl p-6 mb-8">
          <p className="text-sm font-black mb-3">Commentaire (optionnel)</p>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder={isOrga
              ? 'Ex : Stand impeccable, équipe ponctuelle et souriante…'
              : 'Ex : Super organisation, emplacement bien situé, affluence au rendez-vous…'}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50 resize-none"
          />
          {isOrga && (
            <p className="text-[11px] text-emerald-400/80 mt-2 flex items-start gap-1.5">
              <BadgeCheck className="w-3.5 h-3.5 shrink-0 mt-px" />
              Un retour positif (note ≥ 4/5) pourra être publié comme <strong>référence vérifiée</strong> sur le profil public de l'exposant, lié à votre festival.
            </p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full flex items-center justify-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-40 disabled:cursor-not-allowed text-white py-4 rounded-2xl font-black text-sm transition-all"
        >
          <ShieldCheck className="w-4 h-4" />
          {submitting ? 'Envoi en cours…' : 'Envoyer mon avis'}
        </button>
      </div>
    </div>
  );
}
